import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ChevronUp, ChevronDown } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import type { AccountTransaction } from '../../types/financialStatement';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    accountNr: string;
    accountName: string;
    company: string;
    year: number;
    comparisonYear: number;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('de-CH');
}

type SortKey = 'AccountingDate' | 'DocumentNumber' | 'PostingText' | 'AmountLCY' | 'UserName';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, align, className }: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    align?: 'left' | 'right';
    className?: string;
}) {
    const isActive = currentKey === sortKey;
    return (
        <th
            className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 bg-white ${
                align === 'right' ? 'text-right' : 'text-left'
            } ${className || ''}`}
            onClick={() => onSort(sortKey)}
        >
            <span className="inline-flex items-center gap-0.5">
                {align === 'right' && (
                    <span className="inline-flex flex-col -space-y-1">
                        <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                        <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    </span>
                )}
                {label}
                {align !== 'right' && (
                    <span className="inline-flex flex-col -space-y-1">
                        <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                        <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    </span>
                )}
            </span>
        </th>
    );
}

export function TransactionDrillModal({
    isOpen, onClose, accountNr, accountName, company, year, comparisonYear,
}: Props) {
    const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [showYear, setShowYear] = useState<'current' | 'comparison'>('current');
    const [sortKey, setSortKey] = useState<SortKey>('AccountingDate');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    const activeYear = showYear === 'current' ? year : comparisonYear;

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        financialStatementApi.getAccountTransactions({ accountNr, company, year: activeYear })
            .then(result => setTransactions(result.transactions))
            .catch(err => console.error('Failed to load transactions:', err))
            .finally(() => setLoading(false));
    }, [isOpen, accountNr, company, activeYear]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sorted = useMemo(() => {
        const list = [...transactions];
        list.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (typeof av === 'string' && typeof bv === 'string') {
                return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            const an = av as number, bn = bv as number;
            return sortDir === 'asc' ? an - bn : bn - an;
        });
        return list;
    }, [transactions, sortKey, sortDir]);

    if (!isOpen) return null;

    const exportUrl = financialStatementApi.getAccountTransactionsExportUrl({ accountNr, company, year: activeYear });
    const total = transactions.reduce((s, t) => s + t.AmountLCY, 0);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {accountNr} - {accountName}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {company.toUpperCase()} | {activeYear} | {transactions.length} transactions
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Year Switch */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                            <button
                                onClick={() => setShowYear('current')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    showYear === 'current'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {year}
                            </button>
                            <button
                                onClick={() => setShowYear('comparison')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    showYear === 'comparison'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {comparisonYear}
                            </button>
                        </div>
                        <a
                            href={exportUrl}
                            className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1.5 text-sm text-white hover:bg-green-700"
                        >
                            <Download className="h-4 w-4" />
                            Export
                        </a>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-auto overscroll-contain flex-1 px-6 pb-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    ) : (
                        <table className="min-w-full border-separate border-spacing-0">
                            <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
                                {/* Total row above column headers */}
                                <tr className="border-b border-gray-200">
                                    <td className="px-3 py-2 text-sm font-bold bg-white" colSpan={3}>
                                        Total ({transactions.length} entries)
                                    </td>
                                    <td className={`px-3 py-2 text-sm text-right font-bold tabular-nums bg-white ${
                                        total >= 0 ? '' : 'text-red-600'
                                    }`}>{formatAmount(total)}</td>
                                    <td className="bg-white"></td>
                                </tr>
                                <tr className="border-b-2 border-gray-200">
                                    <SortHeader label="Date" sortKey="AccountingDate" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="w-28" />
                                    <SortHeader label="Document" sortKey="DocumentNumber" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="w-32" />
                                    <SortHeader label="Posting Text" sortKey="PostingText" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Amount CHF" sortKey="AmountLCY" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" className="w-32" />
                                    <SortHeader label="User" sortKey="UserName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="w-28" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map((tx, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="px-3 py-1.5 text-sm whitespace-nowrap">{formatDate(tx.AccountingDate)}</td>
                                        <td className="px-3 py-1.5 text-sm font-mono whitespace-nowrap">{tx.DocumentNumber}</td>
                                        <td className="px-3 py-1.5 text-sm truncate max-w-md" title={tx.PostingText}>{tx.PostingText}</td>
                                        <td className={`px-3 py-1.5 text-sm text-right tabular-nums ${
                                            tx.AmountLCY >= 0 ? '' : 'text-red-600'
                                        }`}>{formatAmount(tx.AmountLCY)}</td>
                                        <td className="px-3 py-1.5 text-sm text-gray-500">{tx.UserName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {!loading && transactions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">No transactions found for this account and year.</div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
